import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { environment } from '@environment/environment';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CoreRoutingModule } from '@references/core/core-routing.module';
import { ReferencesSharedModule } from '@references/shared/shared.module';
import { AirbrakeErrorHandler } from '@shared/handlers/airbrake-error.handler';
import { InterceptorService } from '@shared/services/interceptor.service';
import { CustomPreloadingStrategy } from '@shared/strategies/custom.preloading.strategy';
import { ChartModule, HIGHCHARTS_MODULES } from 'angular-highcharts';
import * as more from 'highcharts/highcharts-more.src';
import * as drilldown from 'highcharts/modules/drilldown.src';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { SidebarModule } from 'ng-sidebar';
import { Ng5SliderModule } from 'ng5-slider';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { RatingModule } from 'ngx-bootstrap/rating';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgXCableModule } from 'ngx-cable';
import { ColorPickerModule } from 'ngx-color-picker';
import { CookieService } from 'ngx-cookie-service';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxMaskModule } from 'ngx-mask';
import { PixelModule } from 'ngx-pixel';
import { QuillModule } from 'ngx-quill';
import { NgxSelectModule } from 'ngx-select-ex';
import { SignaturePadModule } from 'ngx-signaturepad';
import { ToastrModule } from 'ngx-toastr';
import { UiSwitchModule } from 'ngx-ui-switch';
import { NgxWebstorageModule } from 'ngx-webstorage';

// AoT requires an exported function for factories
export const createTranslateLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, './assets/i18n/', '.json');

@Injectable({
  providedIn: 'root'
})
@NgModule({
  imports: [
    ReferencesSharedModule,
    ColorPickerModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ChartModule,
    CoreRoutingModule,
    Ng5SliderModule,
    SignaturePadModule,
    GooglePlaceModule,
    NgXCableModule,
    NgxSelectModule,
    NgxMaskModule.forRoot(),
    NgIdleKeepaliveModule.forRoot(),
    PixelModule.forRoot({
      enabled: environment.FB_PIXEL.IS_ENABLED,
      pixelId: environment.FB_PIXEL.PIXEL_ID
    }),
    RatingModule.forRoot(),
    TabsModule.forRoot(),
    ButtonsModule.forRoot(),
    SidebarModule.forRoot(),
    BsDatepickerModule.forRoot(),
    PopoverModule.forRoot(),
    QuillModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    TooltipModule.forRoot(),
    TypeaheadModule.forRoot(),
    PaginationModule.forRoot(),
    SortableModule.forRoot(),
    UiSwitchModule.forRoot({
      size: 'small',
      color: '#21CE99',
      defaultBgColor: '#FF2721',
      defaultBoColor: 'rgba(0, 0, 0, 0.1)'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    LoggerModule.forRoot({
      // serverLoggingUrl: '/logs',
      level: environment.IS_PRODUCTION
        ? NgxLoggerLevel.ERROR
        : NgxLoggerLevel.DEBUG
      // serverLogLevel: NgxLoggerLevel.ERROR
    }),
    NgxWebstorageModule.forRoot({
      prefix: 'references.vitay.io'
    }),
    ToastrModule.forRoot({
      timeOut: 2000,
      toastClass: 'ngx-toastr v-toastr',
      preventDuplicates: true
    }),
    NgCircleProgressModule.forRoot({
      animation: false,
      renderOnClick: false,
      titleColor: '#000000',
      innerStrokeColor: 'rgba(0, 0, 0, 0)',
      space: -3.5,
      outerStrokeWidth: 3,
      showSubtitle: false,
      outerStrokeLinecap: 'round',
      lazy: false
    })
  ],
  exports: [RouterModule],
  declarations: [],
  providers: [
    CustomPreloadingStrategy,
    CookieService,
    {
      provide: ErrorHandler,
      useClass: AirbrakeErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true
    },
    {
      provide: HIGHCHARTS_MODULES,
      useFactory: () => [more, drilldown]
    }
  ]
})
export class CoreModule {}
